import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import { TokenStorage } from '../lib/token-storage';
import type { LLApiResponse, Shopper, ShopperUpdateRequest } from '../types/loyalty-lane';
import { useUserStore } from '@/store/useUserStore';

export function useCustomerProfile() {
  const queryClient = useQueryClient();
  const authenticated = TokenStorage.isAuthenticated();

  const {
    data: profile,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customer-profile'],
    queryFn: async () => {
      const response = await loyaltyLaneClient.get<LLApiResponse<{ shopper: Shopper }>>('Shopper');
      return response.data.data.shopper;
    },
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const updateProfile = useMutation({
    mutationFn: async (data: ShopperUpdateRequest) => {
      const response = await loyaltyLaneClient.put<LLApiResponse<{ shopper: Shopper }>>('Shopper', data);
      return response.data.data.shopper;
    },
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['customer-profile'], updatedProfile);

      useUserStore.getState().setCouponProfile({
        firstName: updatedProfile.FirstName,
        lastName: updatedProfile.LastName,
        email: updatedProfile.Email,
        phone: String(updatedProfile.MobilePhone || updatedProfile.HomePhone || ''),
        shopperid: updatedProfile.ID,
        accountid: updatedProfile.AccountId,
        cardNumber: useUserStore.getState().couponProfile?.cardNumber,
      });
    },
  });

  return {
    profile,
    isLoading,
    error,
    refetch,
    updateProfile,
    isAuthenticated: authenticated,
  };
}
